import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import Slider from "react-slick";
import slugify from "@sindresorhus/slugify";
import classNames from "classnames";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import { Link } from "./link";
import PlayDark from "../icons/play-dark.svg";
import IconPlay from "../icons/icon-play.svg";
import IconPause from "../icons/icon-pause.svg";
import YoutubeContext from "../context/youtube";
import Breadcrumb from "./breadcrumb";
import sortPosition from "../utility/sort-position";
import { CDN_BASE_URL } from "../utility/constants";
import useIsMobile from "../hooks/use-is-mobile";

const NextArrow = (props) => {
  const { handleClick, onClick, currentSlide, slideCount, ...otherProps } = props;
  return (
    <button
      type="button"
      onClick={() => {
        onClick();
        handleClick();
      }}
      className={"slick-prev slick-arrow" + (currentSlide === 0 ? " slick-disabled" : "")}
      aria-hidden="true"
      aria-disabled={currentSlide === 0 ? true : false}
      {...otherProps}
    >
      Next
    </button>
  );
};

const PrevArrow = (props) => {
  const { handleClick, onClick, currentSlide, slideCount, ...otherProps } = props;

  return (
    <button
      type="button"
      onClick={() => {
        onClick();
        handleClick();
      }}
      className={
        "slick-next slick-arrow" + (currentSlide === slideCount - 1 ? " slick-disabled" : "")
      }
      aria-hidden="true"
      aria-disabled={currentSlide === slideCount - 1 ? true : false}
      {...otherProps}
    >
      Prev
    </button>
  );
};

const settings = {
  dots: true,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 5000,
  arrows: true,
  swipe: true,
  slidesToShow: 1,
  responsive: [
    {
      breakpoint: 992,
      settings: {
        arrows: false,
      },
    },
  ],
  pauseOnFocus: true,
};

const SlideContent = ({
  item,
  className,
  isFirst,
  breadcrumb,
  origin,
  element,
  bgColor,
  slider,
  isFirstHero,
  textColor,
}) => {
  const { showVideoModal } = useContext(YoutubeContext);
  const [showIcons, setShowIcons] = useState(false);
  const contentRef = useRef();
  // Show icons if they have space
  const updateShowIcons = useCallback(
    (wrapperEl) => {
      if (wrapperEl) {
        const [, contentEl, iconsEl] = [...wrapperEl.childNodes];

        if (contentEl && iconsEl) {
          const wrapperRect = wrapperEl.getBoundingClientRect();
          const contentRect = contentEl.getBoundingClientRect();
          const iconsrRect = iconsEl.getBoundingClientRect();
          const breadcrumbHeight = 30;

          /* console.log(
          "wrapper, content, icons: ",
          wrapperRect.height,
          contentRect.height,
          iconsrRect.height
        ); */

          /* console.log(contentRect.height + iconsrRect.height + breadcrumbHeight + 50); */

          if (breadcrumb) {
            setShowIcons(
              wrapperRect.height > contentRect.height + iconsrRect.height + breadcrumbHeight + 50
            );
          } else {
            setShowIcons(wrapperRect.height > contentRect.height + iconsrRect.height + 50);
          }
        }
      }
    },
    [breadcrumb]
  );
  // Check until content stops to overflow wrapper, then the slider is initialized, so check if icons have space
  useEffect(() => {
    const wrapperEl = contentRef.current;
    if (wrapperEl) {
      const [contentEl] = [...wrapperEl.childNodes];
      const contentOverflows = (wrapperEl, contentEl) => {
        const wrapperRect = wrapperEl.getBoundingClientRect();
        const contentRect = contentEl.getBoundingClientRect();
        /* console.log("content wrapper: ", contentRect.height, wrapperRect.height); */
        return contentRect.height > wrapperRect.height;
      };
      if (contentEl && contentOverflows(wrapperEl, contentEl)) {
        const intervalId = setInterval(() => {
          if (!contentOverflows(wrapperEl, contentEl)) {
            clearInterval(intervalId);
            updateShowIcons(wrapperEl);
          }
        }, 500);
        return () => clearInterval(intervalId);
      } else {
        updateShowIcons(wrapperEl);
      }
    }
  }, [updateShowIcons]);
  // On resize, check if icons have space
  useEffect(() => {
    const wrapperEl = contentRef.current;
    if (wrapperEl) {
      const calcShowIcons = () => updateShowIcons(wrapperEl);
      window.addEventListener("resize", calcShowIcons);
      return () => {
        window.removeEventListener("resize", calcShowIcons);
      };
    }
  }, [updateShowIcons]);

  return (
    <div
      ref={contentRef}
      className={classNames(
        "slider-hero__content",
        (isFirst || isFirstHero) && "slider-hero__content--before",
        className,
        !isFirst && !isFirstHero && bgColor === "white" && "slider-hero__content--not-first-white",
        !isFirst && !isFirstHero && "slider-hero__content--not-first",
        !isFirst && !isFirstHero && slider && "slider-hero__content--not-first-slider",
        !isFirst &&
          !isFirstHero &&
          !item?.title &&
          !item?.pretitle &&
          !item?.subtitleT1 &&
          !item?.abstract &&
          item?.sliderVideoName &&
          item?.sliderVideoNameMobile &&
          "d-none d-lg-block"
      )}
    >
      {(isFirst || isFirstHero) && <div className="slider-hero__img-overlay d-lg-none"></div>}
      <div className="slider-hero__inner-content">
        {item.pretitle && (
          <>
            <span className="spacer"></span>
            <div
              className={classNames(
                "slider-hero__pretitle",
                !isFirst && !isFirstHero && "slider-hero__pretitle--not-first",
                !isFirst &&
                  !isFirstHero &&
                  bgColor === "white" &&
                  "slider-hero__pretitle--not-first-white",
                textColor && `text-color-${textColor}`
              )}
              dangerouslySetInnerHTML={{ __html: item.pretitle }}
            ></div>
          </>
        )}
        <span className="spacer"></span>
        {isFirst && origin !== "Homepage" && element === 0 ? (
          <h1
            className={classNames(
              "slider-hero__title heading-sm-semibold lg-heading-lg-semibold",
              textColor && `text-color-${textColor}`
            )}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ) : (
          <div
            className={classNames(
              "slider-hero__title heading-sm-semibold lg-heading-lg-semibold",
              !isFirst && !isFirstHero && "slider-hero__title--not-first",
              !isFirst &&
                !isFirstHero &&
                bgColor === "white" &&
                "slider-hero__title--not-first-white",
              textColor && `text-color-${textColor}`
            )}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        )}
        {item.subtitleT1 && (
          <>
            <span className="spacer"></span>
            <div
              className={classNames(
                "slider-hero__subtitle text-sm-light lg-heading-sm-light",
                textColor && `text-color-${textColor}`
              )}
              dangerouslySetInnerHTML={{ __html: item.subtitleT1 }}
            ></div>
          </>
        )}
        {item?.abstract && (
          <>
            <span className="spacer"></span>
            <div
              className={classNames(
                "slider-hero__abstract",
                !isFirst && !isFirstHero && "slider-hero__abstract--not-first",
                !isFirst &&
                  !isFirstHero &&
                  bgColor === "white" &&
                  "slider-hero__abstract--not-first-white",
                textColor && `text-color-${textColor}`
              )}
              dangerouslySetInnerHTML={{ __html: item.abstract }}
            ></div>
          </>
        )}
        <span className="spacer"></span>
        <div className="slider-hero__buttons d-inline-flex">
          {item.link && item.linkLabel && (
            <Link
              className={classNames("slider-hero__btn btn-link button", {
                "order-lg-2": item.right,
                "tertiary": item?.linkColor === "White",
                "primary": item?.linkColor === "Blu",
                "secondary":
                  item?.linkColor === "Grey" ||
                  !item?.linkColor ||
                  item?.linkColor === undefined ||
                  item?.linkColor === "",
              })}
              to={item.link}
            >
              {item.linkLabel}
            </Link>
          )}
          {item.youtube && (
            <button
              className={classNames("btn-play ml-4", item.right && "ml-lg-0 mr-lg-4")}
              name={item.youtube}
              onClick={showVideoModal}
              aria-label="Play"
            >
              <PlayDark />
            </button>
          )}
        </div>
      </div>
      {!!item.icons?.length && (
        <div
          className={classNames(
            "slider-hero__icons d-flex align-items-end justify-content-center pt-4 pt-lg-5",
            {
              "slider-hero__icons--hidden": !showIcons,
              "justify-content-lg-end":
                !item.top && !item.bottom && item.right && !item.slideForegroundImage,
              "justify-content-lg-start":
                !item.top && !item.bottom && !(item.right && !item.slideForegroundImage),
            }
          )}
        >
          {item.icons
            .filter((icon) => !!icon.icon)
            .slice(0, 3)
            .map((icon, j) =>
              icon.link ? (
                <Link
                  className={classNames(
                    "slider-hero__icons-item align-self-start d-flex flex-column align-items-center mx-1 ml-lg-0 slider-hero__icons-link ",
                    item.icons.length - 1 === j ? "mr-lg-0" : "mr-lg-3"
                  )}
                  key={j}
                  to={icon.link}
                  target={icon.target}
                >
                  {icon.iconMobile && (
                    <img
                      src={icon.iconMobile}
                      style={{
                        width: icon.width,
                        height: icon.height,
                      }}
                      alt={icon.alt}
                      className="slider-hero__icon d-lg-none"
                    />
                  )}
                  <img
                    src={icon.icon}
                    style={{
                      width: icon.width,
                      height: icon.height,
                    }}
                    alt={icon.alt}
                    className={classNames(
                      "slider-hero__icon",
                      icon.iconMobile && "d-none d-lg-block"
                    )}
                  />
                  <div className="text-center mt-2">{icon.text}</div>
                </Link>
              ) : (
                <div
                  className={classNames(
                    "slider-hero__icons-item align-self-start d-flex flex-column align-items-center mx-1 ml-lg-0  ",
                    item.icons.length - 1 === j ? "mr-lg-0" : "mr-lg-3"
                  )}
                  key={j}
                >
                  {icon.iconMobile && (
                    <img
                      src={icon.iconMobile}
                      style={{
                        width: icon.width,
                        height: icon.height,
                      }}
                      alt={icon.alt}
                      className="slider-hero__icon d-lg-none"
                    />
                  )}
                  <img
                    src={icon.icon}
                    style={{
                      width: icon.width,
                      height: icon.height,
                    }}
                    alt={icon.alt}
                    className={classNames(
                      "slider-hero__icon",
                      icon.iconMobile && "d-none d-lg-block"
                    )}
                  />
                  <div className="text-center mt-2">{icon.text}</div>
                </div>
              )
            )}
        </div>
      )}
    </div>
  );
};

const SlideFull = ({
  item,
  isFirst,
  breadcrumb,
  origin,
  element,
  isFirstHero,
  pauseSlides,
  textColor,
}) => {
  //const linkRef = useRef();
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classNames(
        "slider-hero__slide"
        // item?.link && !item?.linkLabel && "slider-hero__slide-link-content"
      )}
      onClick={() => {
        // linkRef?.current?.click();
        pauseSlides();
      }}
    >
      {/* {item?.link && !item?.linkLabel ? (
        <Link className="slider-hero__slide-link-no-label" ref={linkRef} to={item.link}>
          {item.link}
        </Link>
      ) : null} */}
      {!!item?.sliderVideoName && !!item?.sliderVideoNameMobile ? (
        <>
          <video
            className="d-none d-lg-block"
            src={`${CDN_BASE_URL}/slider-video/${item.sliderVideoName}`}
            autoPlay
            muted
            playsInline
            loop
            width="100%"
            height="100%"
          ></video>
          <video
            className="d-block d-lg-none"
            src={`${CDN_BASE_URL}/slider-video/${item.sliderVideoNameMobile}`}
            autoPlay
            muted
            playsInline
            loop
            width="100%"
            height="100%"
          ></video>
        </>
      ) : item.bgImage ? (
        <GatsbyImage
          className="slider-hero__image"
          image={item.bgImage}
          loading="eager"
          alt={item.bgImageAlt ? item.bgImageAlt : ""}
        />
      ) : (
        <>
          <picture>
            <source
              srcSet={item.bgImageMobileT1?.node?.publicURL || item.bgImageT1?.node?.publicURL}
              media="(max-width: 1200px)"
            />
            <img
              className="slider-hero__image"
              src={item.bgImageT1.node.publicURL}
              loading="eager"
              alt={item.bgImageT1 ? item.bgImageT1?.altImageDesktop : ""}
            />
          </picture>
        </>
      )}

      {/* {isFirst ? (
        <h1
          className="slider-hero__title--mobile"
          dangerouslySetInnerHTML={{ __html: item.title }}
        />
      ) : (
        <div
          className="slider-hero__title--mobile"
          dangerouslySetInnerHTML={{ __html: item.title }}
        />
      )} */}
      <SlideContent
        className={classNames({
          "slider-hero__content--right": item.right,
          "slider-hero__content--top": item.top,
          "slider-hero__content--bottom": item.bottom,
          "text-center": item.top || item.bottom,
          "slider-hero__content--center": item.top || item.bottom,
        })}
        item={item}
        breadcrumb={breadcrumb}
        isFirst={isFirst}
        origin={origin}
        element={element}
        isFirstHero={isFirstHero}
        textColor={textColor}
      />
    </div>
  );
};

const SlideHalf = ({
  item,
  isFirst,
  breadcrumb,
  origin,
  element,
  className,
  slider,
  isFirstHero,
  pauseSlides,
}) => {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={classNames(
        "slider-hero__slide",
        !isFirst && !isFirstHero && className === "white" && "slider-hero__slide--white"
      )}
      onClick={() => {
        pauseSlides();
      }}
    >
      <div
        className={classNames(
          "row",
          item.bgColor === "white" ? "slider-hero__content--white" : "slider-hero__content--black"
        )}
      >
        <div
          className={classNames(
            "col-lg-6 p-0 m-0",
            !item.right && "order-lg-2",
            !isFirst && !isFirstHero && "slider-hero__wrapper"
          )}
        >
          {item.slideForegroundImage.images ? (
            <GatsbyImage
              className={classNames(
                "slider-hero__image",
                !isFirst && !isFirstHero && "slider-hero__image--half"
              )}
              loading="eager"
              image={item.slideForegroundImage}
              alt={item.imgAlt || ""}
            />
          ) : (
            <>
              <picture>
                <source
                  srcSet={
                    item.bgImageMobileT1?.node?.publicURL ||
                    item.slideForegroundImage.node.publicURL
                  }
                  media="(max-width: 1200px)"
                />
                <img
                  className={classNames(
                    "slider-hero__image",
                    !isFirst && !isFirstHero && "slider-hero__image--half"
                  )}
                  loading="eager"
                  src={item.slideForegroundImage.node.publicURL}
                  alt={item.imgAlt || ""}
                />
              </picture>
            </>
          )}
          {/* {isFirst ? (
            <h1
              className="slider-hero__title--mobile"
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          ) : (
            <div
              className="slider-hero__title--mobile"
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
          )} */}
        </div>
        <div className={classNames("col-lg-6 p-0 m-0 slider-hero__content--border")}>
          <SlideContent
            item={item}
            className={classNames(
              "slider-hero__content--half",
              !isFirst &&
                !isFirstHero &&
                item?.right &&
                "slider-hero__content--half-not-first-right",
              !isFirst &&
                !isFirstHero &&
                !item?.right &&
                "slider-hero__content--half-not-first-left"
            )}
            isFirst={isFirst}
            isFirstHero={isFirstHero}
            breadcrumb={breadcrumb}
            origin={origin}
            element={element}
            bgColor={!isFirst && !isFirstHero ? item?.bgColor : null}
            slider={slider}
          />
        </div>
      </div>
    </div>
  );
};

const ModuleSliderHero = ({
  id,
  module,
  breadcrumb,
  dataArticleId,
  isFirst,
  origin,
  isFirstHero,
}) => {
  const slides = useMemo(
    () =>
      module.titleT1FieldSet
        .filter(
          (item) =>
            item.bgImageT1FieldSet.bgImageT1 ||
            item.slideForegroundImageFieldSet.slideForegroundImage ||
            (item?.sliderVideoName && item?.sliderVideoNameMobile)
        )
        .map((slide, i) => {
          const {
            titleT1,
            positionT1,
            videoT1,
            backgroundHalf,
            labelT1FieldSet,
            bgImageT1FieldSet,
            bgImageMobileT1FieldSet,
            slideForegroundImageFieldSet,
            sliderVideoName,
            sliderVideoNameMobile,
            icons,
            ...content
          } = slide;
          const { bgImageT1 } = bgImageT1FieldSet;
          const { bgImageMobileT1 } = bgImageMobileT1FieldSet;
          const { slideForegroundImage } = slideForegroundImageFieldSet;
          const hFocusImageDesktop = bgImageT1FieldSet?.focusImageDesktop[0]
            ? bgImageT1FieldSet.focusImageDesktop[0].toLowerCase()
            : "center";
          const vFocusImageDesktop = bgImageT1FieldSet?.verticalFocusImageDesktop?.[0]
            ? bgImageT1FieldSet.verticalFocusImageDesktop[0].toLowerCase()
            : "center";
          return {
            title: titleT1,
            ...content,
            pretitle: content?.pretitle,
            id: `${id}-${i}`,
            link: labelT1FieldSet?.linkT1,
            linkLabel: labelT1FieldSet?.labelT1,
            linkColor:
              labelT1FieldSet?.backgroundColor && labelT1FieldSet.backgroundColor.length
                ? labelT1FieldSet.backgroundColor[0]
                : "Grey",
            right: positionT1?.[0] === "right",
            top: !slideForegroundImage?.node && positionT1?.[0] === "top",
            bottom: !slideForegroundImage?.node && positionT1?.[0] === "bottom",
            youtube: videoT1 ? videoT1.split("/").reverse()[0] : null,
            sliderVideoName: sliderVideoName || null,
            sliderVideoNameMobile: sliderVideoNameMobile || null,
            bgImage:
              bgImageT1?.node &&
              bgImageT1?.node?.gatsbyImageData &&
              withArtDirection(getImage(bgImageT1.node), [
                {
                  media: "(max-width: 1200px)",
                  image: getImage(bgImageMobileT1?.node || bgImageT1.node),
                },
              ]),
            bgImageAlt: bgImageT1FieldSet?.altImageDesktop,
            bgColor: backgroundHalf?.[0] || "Black",
            slideForegroundImage:
              slideForegroundImage?.node && slideForegroundImage?.node?.gatsbyImageData
                ? withArtDirection(getImage(slideForegroundImage.node), [
                    {
                      media: "(max-width: 1200px)",
                      image: getImage(bgImageMobileT1?.node || slideForegroundImage.node),
                    },
                  ])
                : slideForegroundImage,
            imgStyle: {
              objectPosition: `${vFocusImageDesktop} ${hFocusImageDesktop}`,
            },
            imgStyleMobile: {
              objectPosition: bgImageMobileT1FieldSet?.focusImageMobile?.[0]
                ? `center ${bgImageMobileT1FieldSet.focusImageMobile[0].toLowerCase()}`
                : "center",
            },
            imgAlt: slideForegroundImageFieldSet && slideForegroundImageFieldSet.altImage,
            icons: icons?.filter(Boolean)?.map((item) => ({
              icon: item.icon?.node?.publicURL,
              iconMobile: item?.iconMobile?.node?.publicURL,
              width: item?.width,
              height: item?.height,
              text: item?.text,
              alt: item?.icon?.value?.description || "",
              link: item?.iconLink,
              target: item?.targetIcon?.[0] || "_blank",
            })),
            mobileRatio:
              (bgImageMobileT1?.node
                ? bgImageMobileT1.node.resize.aspectRatio
                : slideForegroundImage?.node
                ? slideForegroundImage.node.resize?.aspectRatio
                : bgImageT1?.node?.resize?.aspectRatio) || 1,
            breadcrumbBlack:
              slideForegroundImage?.node &&
              positionT1 &&
              positionT1.length &&
              positionT1[0] === "left" &&
              backgroundHalf?.[0] === "white",
          };
        })
        .sort((a, b) => {
          const posA = parseInt(a?.positionSlide?.[0]);
          const posB = parseInt(b?.positionSlide?.[0]);
          return !isNaN(posA) && !isNaN(posB)
            ? posA - posB
            : isNaN(posA) && isNaN(posB)
            ? 0
            : isNaN(posA)
            ? 1
            : -1;
        }),
    [module.titleT1FieldSet, id]
  );

  const { liferayMainBenefits } = useStaticQuery(graphql`
    {
      liferayMainBenefits {
        fieldsGroup {
          mainBenefitsTitle
        }
      }
    }
  `);

  const sliderRef = useRef();
  const [isPlaying, setIsPlaying] = useState(true);
  const playSlides = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slickPlay();
      setIsPlaying(true);
    }
  }, []);
  const pauseSlides = useCallback(() => {
    if (sliderRef.current) {
      sliderRef.current.slickPause();
      setIsPlaying(false);
    }
  }, []);

  const [slide, setSlide] = useState(0);
  const sliderSettings = useMemo(
    () => ({
      ...settings,
      nextArrow: <NextArrow handleClick={pauseSlides} />,
      prevArrow: <PrevArrow handleClick={pauseSlides} />,
      beforeChange: (prev, next) => setSlide(next),
    }),
    [pauseSlides]
  );
  const isMobile = useIsMobile(992);

  useEffect(() => {
    if (sliderRef.current) {
      const resizeListener = () => {
        isMobile ? pauseSlides() : playSlides();
      };
      setTimeout(resizeListener, 10);
      window.addEventListener("resize", resizeListener);
      return () => window.removeEventListener("resize", resizeListener);
    }
  }, [isMobile, playSlides, pauseSlides]);

  return (
    <>
      {module.anchorDesktop && (
        <span id={slugify(module.anchorDesktop)} className="tabs__scroll-anchor" />
      )}
      <div
        className={classNames("slider-hero", !isFirst && !isFirstHero && "slider-hero--not-first")}
        data-articleid={dataArticleId}
        style={{
          "--mobile-ratio": `${100 / Math.min(...slides.map((slide) => slide.mobileRatio))}vw`,
        }}
      >
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 px-0 postion-relative">
              {breadcrumb && (
                <Breadcrumb
                  className={classNames(
                    "breadcrumb--absolute",
                    slides[slide].breadcrumbBlack && "breadcrumb--dark"
                  )}
                  breadcrumb={breadcrumb}
                />
              )}
              <Slider
                ref={(slider) => (sliderRef.current = slider)}
                {...sliderSettings}
                appendDots={(dots) => (
                  <div>
                    <ul className="slick-dots-list">{dots}</ul>
                    <div className="ml-3">
                      {isPlaying ? (
                        <button
                          className="slick-dots-play"
                          onClick={pauseSlides}
                          aria-label="Slider Pause"
                        >
                          <IconPause />
                        </button>
                      ) : (
                        <button
                          className="slick-dots-play"
                          onClick={playSlides}
                          aria-label="Slider Play"
                        >
                          <IconPlay />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                className={classNames("slider-hero__slider", {
                  "slider-hero__slider--with-benefits": liferayMainBenefits?.fieldsGroup?.length,
                })}
              >
                {slides.map((item, i) =>
                  item.slideForegroundImage ? (
                    <SlideHalf
                      item={item}
                      isFirst={isFirst}
                      isFirstHero={isFirstHero}
                      key={i}
                      breadcrumb={breadcrumb}
                      origin={origin}
                      element={i}
                      className={
                        slides?.find((slide) => slide?.bgColor !== "white") ? "black" : "white"
                      }
                      slider={slides?.length > 1}
                      pauseSlides={pauseSlides}
                    />
                  ) : (
                    <SlideFull
                      item={item}
                      isFirst={isFirst}
                      isFirstHero={isFirstHero}
                      key={i}
                      breadcrumb={breadcrumb}
                      origin={origin}
                      element={i}
                      pauseSlides={pauseSlides}
                      textColor={item?.textColor}
                    />
                  )
                )}
              </Slider>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SliderHero = ({ data, breadcrumb, isFirst, origin, isFirstHero }) => {
  const modules = useMemo(
    () =>
      data.moduleTitleFieldSet.length
        ? data.moduleTitleFieldSet
            .filter((module) =>
              module.titleT1FieldSet.some(
                (item) =>
                  item.bgImageT1FieldSet?.bgImageT1 ||
                  item.slideForegroundImageFieldSet?.slideForegroundImage ||
                  (item?.sliderVideoName && item?.sliderVideoNameMobile)
              )
            )
            .sort(sortPosition)
        : data.moduleTitleFieldSet,
    [data.moduleTitleFieldSet]
  );
  return (
    <>
      {modules.map((module, i) => (
        <ModuleSliderHero
          id={`id-${data.id}-${i}`}
          dataArticleId={data.liferayFields.articleId}
          module={module}
          breadcrumb={i === 0 ? breadcrumb : null}
          key={i}
          isFirst={isFirst}
          isFirstHero={isFirstHero}
          origin={origin}
        />
      ))}
    </>
  );
};

export default SliderHero;
export const fragment = graphql`
  fragment SliderHeroFragment on LiferaySliderHero {
    id
    liferayFields {
      siteId
      articleId
    }
    moduleTitleFieldSet {
      anchorDesktop
      anchorMobile
      position
      pageType
      titleT1FieldSet {
        titleT1
        positionSlide
        pretitle
        backgroundHalf
        subtitleT1
        abstract
        textColor
        bgImageT1FieldSet {
          bgImageT1 {
            node {
              gatsbyImageData(layout: FULL_WIDTH)
              resize(width: 1200) {
                src
                width
                height
                aspectRatio
              }
              publicURL
            }
          }
          focusImageDesktop
          verticalFocusImageDesktop
          altImageDesktop
        }
        bgImageMobileT1FieldSet {
          bgImageMobileT1 {
            node {
              gatsbyImageData(layout: FULL_WIDTH)
              resize {
                aspectRatio
                src
                width
                height
              }
              publicURL
            }
          }
          focusImageMobile
        }
        slideForegroundImageFieldSet {
          slideForegroundImage {
            node {
              gatsbyImageData(layout: CONSTRAINED, width: 960)
              resize {
                src
                width
                height
                aspectRatio
              }
              publicURL
            }
          }
          focusForegroundImage
          altImage
        }
        positionT1
        labelT1FieldSet {
          labelT1
          backgroundColor
          linkT1
        }
        videoT1
        sliderVideoName
        sliderVideoNameMobile
        icons {
          icon {
            node {
              publicURL
            }
          }
          width
          height
          text
          iconLink
          targetIcon
          iconMobile {
            node {
              publicURL
            }
          }
        }
      }
    }
  }
`;
